import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRightIcon,
  BicycleIcon,
  CalendarBlankIcon,
  CableCarIcon,
  CheckCircleIcon,
  CloudRainIcon,
  CrosshairIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MountainsIcon,
  PathIcon,
  SpinnerGapIcon,
  SunIcon,
} from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { parkPath } from "@/lib/routes";
import { destinationCoverImage } from "@/lib/destinationCover";
import { compactDayTicketLabel } from "@/lib/compareSpots";
import { useRideMode } from "@/context/RideModeContext";
import { useUnitPreference } from "@/context/UnitPreferenceContext";
import { useAuth } from "@/context/AuthContext";
import { formatDistanceKm } from "@/lib/units";
import { precipitationLabel, temperatureRangeLabel, weatherConditionLabel } from "@/lib/weatherPresentation";
import { trackEvent } from "@/lib/analytics";

const DRIVE_OPTIONS = [
  { minutes: 60, label: "1h", candidateRadiusKm: 120 },
  { minutes: 120, label: "2h", candidateRadiusKm: 220 },
  { minutes: 180, label: "3h", candidateRadiusKm: 350 },
  { minutes: 240, label: "4h", candidateRadiusKm: 500 },
];
const WANT_OPTIONS = [
  { key: "FLOW", label: "Flow" },
  { key: "TECHNICAL", label: "Technical" },
  { key: "JUMPS", label: "Jumps" },
  { key: "DROPS", label: "Drops" },
  { key: "STEEP", label: "Steep" },
  { key: "ROOTS", label: "Roots" },
  { key: "ROCKY", label: "Rocky" },
  { key: "BEGINNER", label: "Beginner" },
];
const ACCESS_OPTIONS = [
  { key: "any", label: "Any", value: "" },
  { key: "lift", label: "Lift", value: "lift" },
  { key: "pedal", label: "Pedal", value: "pedal" },
];

const WEATHER_OPTIONS = [
  { key: "CLEAR", label: "Clear" },
  { key: "PARTLY_CLOUDY", label: "Partly cloudy" },
  { key: "CLOUDY", label: "Cloudy" },
  { key: "FOG", label: "Fog" },
  { key: "DRIZZLE", label: "Drizzle" },
  { key: "RAIN", label: "Rain" },
  { key: "SNOW", label: "Snow" },
  { key: "THUNDERSTORM", label: "Thunderstorm" },
];

const rideModeEnum = (value) => ({
  solo: "SOLO",
  friends: "FRIENDS",
  "family-kids": "FAMILY_KIDS",
  usual: "MY_USUAL",
}[value] || "MY_USUAL");

const rideModeLabel = (value) => ({
  solo: translateLabel("Solo"),
  friends: translateLabel("Friends"),
  "family-kids": translateLabel("Family / kids"),
  usual: translateLabel("My usual ride"),
}[value] || translateLabel("My usual ride"));

const isoDate = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

const nextWeekday = (weekday) => {
  const now = new Date();
  const date = new Date(now);
  let delta = (weekday - now.getDay() + 7) % 7;
  if (delta === 0) delta = 7;
  date.setDate(now.getDate() + delta);
  return date;
};

const dateOptions = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return [
    { key: "tomorrow", label: "Tomorrow", date: isoDate(tomorrow) },
    { key: "saturday", label: "Saturday", date: isoDate(nextWeekday(6)) },
    { key: "sunday", label: "Sunday", date: isoDate(nextWeekday(0)) },
  ].filter((item, index, all) => all.findIndex((candidate) => candidate.date === item.date) === index);
};

const prettyDate = (value) => {
  if (!value) return translateLabel("Not set");
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    }).format(new Date(`${value}T12:00:00`));
  } catch {
    return value;
  }
};

const weatherIcon = (condition) => {
  if (["RAIN", "DRIZZLE", "THUNDERSTORM", "SNOW"].includes(condition)) return CloudRainIcon;
  return SunIcon;
};

export default function RidePlanner() {
  const navigate = useNavigate();
  const { rideMode } = useRideMode();
  const { unitSystem } = useUnitPreference();
  const { user } = useAuth();
  const dates = useMemo(dateOptions, []);
  const [selectedDate, setSelectedDate] = useState(dates[0]?.date || isoDate(new Date()));
  const [maxDriveMinutes, setMaxDriveMinutes] = useState(120);
  const [wants, setWants] = useState([]);
  const [access, setAccess] = useState("");
  const [weatherConditions, setWeatherConditions] = useState([]);
  const [origin, setOrigin] = useState(null);
  const [originLabel, setOriginLabel] = useState("");
  const [placeQuery, setPlaceQuery] = useState("");
  const [locating, setLocating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [groupCreateError, setGroupCreateError] = useState("");
  const [locationError, setLocationError] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!user || user === false || origin) return undefined;
    let active = true;
    api.get("/rider-profile")
      .then(({ data }) => {
        if (!active) return;
        const home = data?.home_location;
        if (!home || !Number.isFinite(home.lat) || !Number.isFinite(home.lng)) return;
        setOrigin({ lat: home.lat, lng: home.lng });
        setOriginLabel(home.name || translateLabel("Home location"));
        setPlaceQuery(home.name || "");
      })
      .catch(() => {});
    return () => { active = false; };
  }, [user, origin]);

  const useCurrentLocation = () => {
    setLocationError("");
    if (!navigator.geolocation) {
      setLocationError(translateLabel("Your browser does not support location."));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const nextOrigin = { lat: coords.latitude, lng: coords.longitude };
        let label = translateLabel("Current location");
        try {
          const { data } = await api.get("/reverse-geocode", { params: nextOrigin });
          if (data?.name) label = data.name;
        } catch {
          // Coordinates are enough for planning even when reverse geocoding is unavailable.
        }
        setOrigin(nextOrigin);
        setOriginLabel(label);
        setPlaceQuery(label);
        setLocating(false);
      },
      () => {
        setLocationError(translateLabel("Location permission was not available. Search for a place instead."));
        setLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  const resolvePlace = async () => {
    const query = placeQuery.trim();
    if (!query) return;
    setLocating(true);
    setLocationError("");
    try {
      const response = await api.get("/geocode", { params: { q: query } });
      const first = response.data?.results?.[0];
      if (!first) {
        setLocationError(translateLabel("No matching place found."));
        return;
      }
      setOrigin({ lat: first.lat, lng: first.lng });
      setOriginLabel(first.name || query);
      setPlaceQuery(first.name || query);
    } catch (requestError) {
      setLocationError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setLocating(false);
    }
  };

  const findParks = async () => {
    if (!origin) {
      setLocationError(translateLabel("Choose your starting point first."));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const driveOption = DRIVE_OPTIONS.find((item) => item.minutes === maxDriveMinutes) || DRIVE_OPTIONS[1];
      const response = await api.post("/ride-planner", {
        date: selectedDate,
        lat: origin.lat,
        lng: origin.lng,
        radius_km: driveOption.candidateRadiusKm,
        max_drive_minutes: maxDriveMinutes,
        type: "bikepark",
        wants,
        access: access ? [access] : undefined,
        weather_conditions: weatherConditions.length ? weatherConditions : undefined,
        ride_mode: rideModeEnum(rideMode),
        limit: 10,
      });
      setResult(response.data);
      trackEvent("ride_planner_search", {
        result_count: response.data?.recommendations?.length ?? 0,
        max_drive_minutes: maxDriveMinutes,
        wants_count: wants.length,
        access: access || "any",
        weather_conditions: weatherConditions.join(",") || "any",
        ride_mode: rideModeEnum(rideMode),
      });
      window.requestAnimationFrame(() => {
        document.querySelector("[data-testid='ride-planner-results']")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (requestError) {
      setError(formatApiErrorDetail(requestError.response?.data?.detail));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const recommendations = result?.recommendations ?? [];

  const createGroupPlan = async () => {
    if (!user) return;
    if (!origin || recommendations.length === 0) return;
    setCreatingGroup(true);
    setGroupCreateError("");
    try {
      const response = await api.post("/ride-plans", {
        date: selectedDate,
        origin: {
          label: originLabel || translateLabel("Starting point"),
          lat: origin.lat,
          lng: origin.lng,
        },
        max_drive_minutes: maxDriveMinutes,
        wants,
        access: access || undefined,
        ride_mode: rideModeEnum(rideMode),
        candidates: recommendations.map((recommendation) => ({
          destination_id: recommendation.park.id,
          name: recommendation.park.name,
          city: recommendation.park.city || null,
          country: recommendation.park.country || null,
          lat: recommendation.park.lat ?? null,
          lng: recommendation.park.lng ?? null,
          drive_minutes: recommendation.travel?.drive_minutes ?? null,
          drive_km: recommendation.travel?.drive_km ?? null,
          weather_condition: recommendation.weather?.condition ?? null,
          weather_temp_min_c: recommendation.weather?.temp_min_c ?? null,
          weather_temp_max_c: recommendation.weather?.temp_max_c ?? null,
          precipitation_probability_pct: recommendation.weather?.precipitation_probability_pct ?? null,
          day_ticket_price: recommendation.day_ticket_price ?? null,
          image: destinationCoverImage(recommendation.park),
        })),
      });
      trackEvent("ride_plan_created", {
        candidate_count: recommendations.length,
        max_drive_minutes: maxDriveMinutes,
        wants_count: wants.length,
        access: access || "any",
        ride_mode: rideModeEnum(rideMode),
      });
      navigate(`/ride-plan/${response.data.room.plan.share_token}`);
    } catch (requestError) {
      setGroupCreateError(formatApiErrorDetail(requestError.response?.data?.detail));
    } finally {
      setCreatingGroup(false);
    }
  };

  const selectedWantsLabel = wants.length
    ? wants.map((key) => WANT_OPTIONS.find((item) => item.key === key)?.label || key).join(", ")
    : "Anything";
  const selectedAccessLabel = ACCESS_OPTIONS.find((option) => option.value === access)?.label || "Any";
  const selectedWeatherLabel = weatherConditions.length
    ? weatherConditions.map((key) => WEATHER_OPTIONS.find((item) => item.key === key)?.label || key).join(", ")
    : "Any";

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white" data-testid="ride-planner-page">
      <Navbar scrollsWithPage />
      <main className="mx-auto w-full max-w-[1500px] px-4 pb-16 pt-24 sm:px-6 lg:px-8 md:pt-28">
        <header className="flex flex-col gap-4 border-b border-white/[0.07] pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#CCFF00]">
              <BicycleIcon size={16} weight="bold" /> Ride planner
            </div>
            <h1 className="mt-2 font-display text-4xl font-black uppercase leading-[0.92] tracking-tight sm:text-5xl md:text-6xl">
              Plan the ride, not the research.
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-400 sm:text-base">
              Tell BPF when, where from and what kind of riding you want. We will build a shortlist using drive time, weather, opening data and trail character.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start rounded-full border border-white/10 bg-white/[0.035] px-3 py-2 text-xs font-bold text-zinc-400 lg:self-auto">
            <CheckCircleIcon size={15} className="text-[#CCFF00]" weight="fill" />
            {rideModeLabel(rideMode)}
          </div>
        </header>

        <section className="mt-6 grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-4">
            <PlannerSection
              step="01"
              icon={<CalendarBlankIcon size={18} />}
              title={translateLabel("When are you riding?")}
              description={translateLabel("Pick the day so opening status and weather match the actual ride.")}
            >
              <div className="flex flex-wrap gap-2">
                {dates.map((item) => (
                  <ChoicePill key={item.date} active={selectedDate === item.date} onClick={() => setSelectedDate(item.date)}>
                    <span>{translateLabel(item.label)}</span>
                    <span className={selectedDate === item.date ? "text-black/55" : "text-zinc-600"}>{prettyDate(item.date)}</span>
                  </ChoicePill>
                ))}
              </div>
            </PlannerSection>

            <PlannerSection
              step="02"
              icon={<MapPinIcon size={18} />}
              title={translateLabel("Where are you starting?")}
              description={translateLabel("Your saved Home location is used automatically when available. You can override it here.")}
            >
              <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                <div className="relative flex min-w-0 rounded-xl border border-white/10 bg-black/20 focus-within:border-[#CCFF00]/40">
                  <MagnifyingGlassIcon size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                  <input
                    value={placeQuery}
                    onChange={(event) => setPlaceQuery(event.target.value)}
                    onKeyDown={(event) => { if (event.key === "Enter") resolvePlace(); }}
                    placeholder={translateLabel("City, postcode or place")}
                    className="min-w-0 flex-1 bg-transparent py-3 pl-10 pr-20 text-sm text-white outline-none placeholder:text-zinc-600"
                    data-testid="ride-planner-place"
                  />
                  <button
                    type="button"
                    onClick={resolvePlace}
                    disabled={locating || !placeQuery.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg px-2.5 py-1.5 text-xs font-extrabold text-[#CCFF00] disabled:opacity-40"
                  >
                    {translateLabel("Set")}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={useCurrentLocation}
                  disabled={locating}
                  className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3 text-sm font-bold text-zinc-200 transition-colors hover:bg-white/[0.07] disabled:opacity-50"
                  data-testid="ride-planner-current-location"
                >
                  {locating ? <SpinnerGapIcon size={17} className="animate-spin" /> : <CrosshairIcon size={17} />}
                  {translateLabel("My location")}
                </button>
              </div>
              {originLabel && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#CCFF00]/[0.08] px-2.5 py-1 text-xs font-bold text-[#CCFF00]">
                  <MapPinIcon size={13} weight="fill" /> {originLabel}
                </div>
              )}
              {locationError && <p className="mt-2 text-xs font-semibold text-red-300">{locationError}</p>}
            </PlannerSection>

            <PlannerSection
              step="03"
              icon={<BicycleIcon size={18} />}
              title={translateLabel("How far do you want to drive?")}
              description={translateLabel("This is a real drive-time limit when routing is available.")}
            >
              <div className="grid grid-cols-4 gap-2 sm:max-w-xl">
                {DRIVE_OPTIONS.map((option) => (
                  <ChoicePill
                    key={option.minutes}
                    active={maxDriveMinutes === option.minutes}
                    onClick={() => setMaxDriveMinutes(option.minutes)}
                    centered
                  >
                    <span>{translateLabel(option.label)}</span>
                  </ChoicePill>
                ))}
              </div>
            </PlannerSection>

            <PlannerSection
              step="04"
              icon={<PathIcon size={18} />}
              title={translateLabel("What kind of ride do you want?")}
              description={translateLabel("Choose only what matters. Leaving this open gives BPF more options.")}
            >
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-[0.15em] text-zinc-600">{translateLabel("Trail character")}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <ChoicePill active={wants.length === 0} onClick={() => setWants([])}>
                    <span>{translateLabel("Any")}</span>
                  </ChoicePill>
                  {WANT_OPTIONS.map((option) => (
                    <ChoicePill
                      key={option.key}
                      active={wants.includes(option.key)}
                      onClick={() => setWants((current) =>
                        current.includes(option.key)
                          ? current.filter((item) => item !== option.key)
                          : [...current, option.key]
                      )}
                    >
                      <span>{translateLabel(option.label)}</span>
                    </ChoicePill>
                  ))}
                </div>
              </div>

              <div className="mt-5 border-t border-white/[0.06] pt-4">
                <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-zinc-600">
                  <CableCarIcon size={13} /> {translateLabel("Access")}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {ACCESS_OPTIONS.map((option) => (
                    <ChoicePill key={option.key} active={access === option.value} onClick={() => setAccess(option.value)}>
                      <span>{translateLabel(option.label)}</span>
                    </ChoicePill>
                  ))}
                </div>
              </div>

              <div className="mt-5 border-t border-white/[0.06] pt-4">
                <div className="flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-[0.15em] text-zinc-600">
                  <SunIcon size={13} /> {translateLabel("Weather condition")}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <ChoicePill active={weatherConditions.length === 0} onClick={() => setWeatherConditions([])}>
                    <span>{translateLabel("Any")}</span>
                  </ChoicePill>
                  {WEATHER_OPTIONS.map((option) => (
                    <ChoicePill
                      key={option.key}
                      active={weatherConditions.includes(option.key)}
                      onClick={() => setWeatherConditions((current) =>
                        current.includes(option.key)
                          ? current.filter((item) => item !== option.key)
                          : [...current, option.key]
                      )}
                    >
                      <span>{translateLabel(option.label)}</span>
                    </ChoicePill>
                  ))}
                </div>
                <p className="mt-2 text-[11px] leading-5 text-zinc-600">
                  {translateLabel("Matches the forecast for the selected ride day. Parks without forecast data are excluded when this filter is active.")}
                </p>
              </div>
            </PlannerSection>
          </div>

          <aside className="overflow-hidden rounded-3xl border border-white/10 bg-[#121214] shadow-[0_20px_70px_rgba(0,0,0,0.18)] xl:sticky xl:top-24">
            <div className="border-b border-white/[0.07] bg-[radial-gradient(circle_at_top_right,rgba(204,255,0,0.12),transparent_42%)] px-5 py-5">
              <div className="text-[10px] font-extrabold uppercase tracking-[0.17em] text-[#CCFF00]">{translateLabel("Your ride")}</div>
              <h2 className="mt-1 font-display text-2xl font-black uppercase text-white">{translateLabel("Ready to shortlist")}</h2>
            </div>

            <div className="divide-y divide-white/[0.06] px-5">
              <SummaryRow label={translateLabel("When")} value={prettyDate(selectedDate)} />
              <SummaryRow label={translateLabel("From")} value={originLabel || translateLabel("Choose a starting point")} muted={!originLabel} />
              <SummaryRow label={translateLabel("Max drive")} value={formatDriveMinutes(maxDriveMinutes)} />
              <SummaryRow label={translateLabel("Ride")} value={selectedWantsLabel} />
              <SummaryRow label={translateLabel("Access")} value={selectedAccessLabel} />
              <SummaryRow label={translateLabel("Weather")} value={selectedWeatherLabel} />
            </div>

            <div className="p-5">
              <button
                type="button"
                onClick={findParks}
                disabled={loading || !origin}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#CCFF00] px-5 py-3.5 text-sm font-extrabold text-black transition-colors hover:bg-[#B8E600] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="ride-planner-submit"
              >
                {loading ? <SpinnerGapIcon size={18} className="animate-spin" /> : <MountainsIcon size={18} weight="bold" />}
                {loading ? translateLabel("Building shortlist…") : translateLabel("Find my ride")}
              </button>
              {!origin && <p className="mt-2 text-center text-xs text-zinc-600">{translateLabel("Set a starting point to continue.")}</p>}
              {error && <p className="mt-3 text-xs font-semibold text-red-300">{error}</p>}

              <div className="mt-4 grid grid-cols-2 gap-2 text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-600">
                <span>{translateLabel("Drive time")}</span>
                <span>{translateLabel("Weather")}</span>
                <span>{translateLabel("Opening data")}</span>
                <span>{translateLabel("Trail fit")}</span>
              </div>
            </div>
          </aside>
        </section>

        {result && (
          <section className="scroll-mt-24 mt-10 border-t border-white/[0.07] pt-8" data-testid="ride-planner-results">
            {!result.routing_available && (
              <div className="mb-4 rounded-2xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm font-semibold text-amber-200">
                {translateLabel("Drive-time routing is temporarily unavailable. Results are shown from the geographic candidate area instead.")}
              </div>
            )}

            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#CCFF00]">{translateLabel("Shortlist")}</div>
                <h2 className="mt-1 font-display text-3xl font-black uppercase sm:text-4xl">
                  {translateLabel(recommendations.length === 1 ? "{count} park worth a look" : "{count} parks worth a look", { count: recommendations.length })}
                </h2>
                <p className="mt-1 text-sm text-zinc-500">
                  {originLabel} · {formatDriveMinutes(maxDriveMinutes)} max · {prettyDate(selectedDate)}
                </p>
              </div>

              {recommendations.length > 0 && (
                <div className="flex flex-col items-start gap-2 sm:items-end">
                  {user ? (
                    <button
                      type="button"
                      onClick={createGroupPlan}
                      disabled={creatingGroup}
                      className="flex items-center gap-2 rounded-xl border border-[#CCFF00]/25 bg-[#CCFF00]/[0.07] px-4 py-2.5 text-sm font-extrabold text-[#CCFF00] transition-colors hover:bg-[#CCFF00]/10 disabled:opacity-60"
                      data-testid="ride-planner-plan-with-group"
                    >
                      {creatingGroup ? <SpinnerGapIcon size={17} className="animate-spin" /> : <BicycleIcon size={17} weight="bold" />}
                      {translateLabel(creatingGroup ? "Creating…" : "Plan with group")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate(`/login?returnTo=${encodeURIComponent("/ride-planner")}`)}
                      className="rounded-xl border border-white/10 px-4 py-2.5 text-sm font-bold text-zinc-300 hover:bg-white/[0.04]"
                      data-testid="ride-planner-plan-with-group-signin"
                    >
                      {translateLabel("Sign in for group planning")}
                    </button>
                  )}
                  {groupCreateError && (
                    <p className="max-w-sm text-xs font-semibold text-red-300" data-testid="ride-planner-group-error">
                      {groupCreateError}
                    </p>
                  )}
                </div>
              )}
            </div>

            {recommendations.length === 0 ? (
              <div className="rounded-3xl border border-white/10 bg-[#121214] p-10 text-center">
                <MountainsIcon size={40} className="mx-auto text-zinc-600" />
                <h3 className="mt-3 font-display text-xl font-bold">{translateLabel("No matching parks")}</h3>
                <p className="mt-1 text-sm text-zinc-400">{translateLabel("Try a longer drive time or fewer trail/access requirements.")}</p>
              </div>
            ) : (
              <div className="grid items-start gap-3 md:grid-cols-2 xl:grid-cols-3">
                {recommendations.map((recommendation) => (
                  <RecommendationCard
                    key={recommendation.park.id}
                    recommendation={recommendation}
                    unitSystem={unitSystem}
                    to={parkPath(recommendation.park)}
                  />
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

function PlannerSection({ step, icon, title, description, children }) {
  return (
    <section className="rounded-3xl border border-white/[0.08] bg-[#121214] p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-9 min-w-9 items-center justify-center rounded-xl border border-[#CCFF00]/15 bg-[#CCFF00]/[0.06] text-[#CCFF00]">
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-zinc-600">{step}</span>
            <h2 className="font-display text-xl font-black uppercase text-white sm:text-2xl">{title}</h2>
          </div>
          <p className="mt-1 text-xs leading-5 text-zinc-500 sm:text-sm">{description}</p>
        </div>
      </div>
      <div className="mt-4 sm:pl-12">{children}</div>
    </section>
  );
}

function ChoicePill({ active, onClick, children, centered = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-bold transition-colors ${
        centered ? "justify-center" : ""
      } ${
        active
          ? "border-[#CCFF00] bg-[#CCFF00] text-black"
          : "border-white/[0.08] bg-white/[0.035] text-zinc-300 hover:border-white/15 hover:bg-white/[0.06]"
      }`}
    >
      {children}
    </button>
  );
}

function SummaryRow({ label, value, muted = false }) {
  return (
    <div className="grid grid-cols-[5.2rem_minmax(0,1fr)] gap-3 py-3.5">
      <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-zinc-600">{label}</div>
      <div className={`min-w-0 text-right text-sm font-bold ${muted ? "text-zinc-600" : "text-zinc-200"}`}>{value}</div>
    </div>
  );
}

function RecommendationCard({ recommendation, unitSystem, to }) {
  const park = recommendation.park;
  const weather = recommendation.weather;
  const WeatherIcon = weatherIcon(weather?.condition);
  const profile = recommendation.trail_profile || {};
  const compactPrice = compactDayTicketLabel(recommendation.day_ticket_price) || recommendation.day_ticket_price;
  const profileItems = [
    ["Flow", profile.flow],
    ["Technical", profile.technical],
    ["Jumps", profile.jumps],
    ["Beginner", profile.beginner],
  ].filter(([, count]) => Number(count) > 0);
  const primaryReasons = recommendation.decision_reasons?.slice(0, 2) || [];
  const warning = recommendation.warnings?.[0]
    || (recommendation.want_match_status === "POSSIBLE"
      ? "Some requested ride traits have incomplete metadata"
      : null);

  return (
    <article className="overflow-hidden rounded-2xl border border-white/[0.08] bg-[#121214]" data-testid="ride-planner-card">
      <div className="relative h-24 overflow-hidden">
        <img src={destinationCoverImage(park)} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-[#121214]/15 to-black/20" />
        <span className={`absolute left-3 top-3 rounded-full px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide ${
          recommendation.planned_date_status === "OPEN"
            ? "bg-emerald-400 text-black"
            : recommendation.planned_date_status === "CLOSED"
              ? "bg-red-500 text-white"
              : "bg-zinc-700/90 text-white"
        }`}>
          {recommendation.planned_date_status === "OPEN"
            ? translateLabel("Open that day")
            : recommendation.planned_date_status === "CLOSED"
              ? translateLabel("Closed that day")
              : translateLabel("Schedule unknown")}
        </span>
        {compactPrice && (
          <span className="absolute right-3 top-3 rounded-full bg-black/70 px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur">
            {compactPrice}
          </span>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-black leading-tight text-white">{park.name}</h3>
            <p className="mt-1 truncate text-xs font-semibold text-zinc-600">
              {[park.city, park.country].filter(Boolean).join(" · ")}
            </p>
          </div>
          <Link
            to={to}
            onClick={() => trackEvent("ride_planner_park_open", {
              park_id: String(park.id),
              park_name: park.name || "",
            })}
            className="shrink-0 text-xs font-bold text-zinc-400 transition-colors hover:text-[#CCFF00]"
          >
            {translateLabel("View park")}
          </Link>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-1.5">
          <Metric
            icon={<MapPinIcon size={13} />}
            label={translateLabel(recommendation.travel?.drive_minutes != null ? "Drive" : "Distance")}
            value={
              recommendation.travel?.drive_minutes != null
                ? `${formatDriveMinutes(recommendation.travel.drive_minutes)}${recommendation.travel?.drive_km != null ? ` · ${formatDistanceKm(recommendation.travel.drive_km, unitSystem)}` : ""}`
                : recommendation.travel?.distance_km != null
                  ? formatDistanceKm(recommendation.travel.distance_km, unitSystem)
                  : "—"
            }
            accent
          />
          <Metric
            icon={<WeatherIcon size={13} />}
            label={weather ? weatherConditionLabel(weather.condition) : translateLabel("Weather")}
            value={weather ? `${temperatureRangeLabel(weather)} · ${precipitationLabel(weather)}` : translateLabel("Unavailable")}
          />
        </div>

        {primaryReasons.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {primaryReasons.map((reason) => (
              <span key={reason} className="rounded-md bg-[#CCFF00]/[0.07] px-2 py-1 text-[10px] font-bold text-[#CCFF00]">
                {reason}
              </span>
            ))}
          </div>
        )}

        {profileItems.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {profileItems.map(([label, count]) => (
              <span key={label} className="rounded-md bg-white/[0.035] px-1.5 py-0.5 text-[10px] font-semibold text-zinc-500">
                {label} · {count}
              </span>
            ))}
          </div>
        )}

        {warning && (
          <p className="mt-2 line-clamp-2 text-[10px] leading-4 text-amber-300">{warning}</p>
        )}

        <div className="mt-2 flex items-center justify-between gap-3 border-t border-white/[0.06] pt-2">
          <div className="truncate text-[10px] text-zinc-700">
            {recommendation.last_verified_at ? translateLabel("Checked {date}", { date: recommendation.last_verified_at }) : translateLabel("Verification date unavailable")}
          </div>
          <ArrowRightIcon size={13} className="shrink-0 text-zinc-700" weight="bold" />
        </div>
      </div>
    </article>
  );
}

function formatDriveMinutes(minutes) {
  const rounded = Math.max(0, Math.round(minutes));
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  if (hours === 0) return `${rest} min`;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

function Metric({ icon, label, value, accent = false }) {
  return (
    <div className="rounded-lg bg-white/[0.035] p-2.5">
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-zinc-600">
        <span className="text-[#CCFF00]">{icon}</span>{translateLabel(label)}
      </div>
      <div className={`mt-0.5 text-[11px] font-bold leading-4 ${accent ? "text-[#CCFF00]" : "text-zinc-300"}`}>{value}</div>
    </div>
  );
}
